import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, PRIMARY_OUTLET, Router } from '@angular/router';

import { LoggerService } from '../../core/services/logger.service';
import { getOperatorFacingRoutePaths } from '../../app.routes';
import { describeUiHighlightTargets } from './copilot-anchors';
import { DashboardFilterStore } from '../dashboard/stores/dashboard-filter.store';
import { AgentComponentCatalogService, CatalogEntry } from './agent-component-catalog.service';

/**
 * M2 — client-side tool registry.
 *
 * AG-UI supports tools the AGENT decides to call but the FRONTEND executes
 * (navigate the operator somewhere, highlight a widget, open a view). The agent
 * is told these tools exist; when it calls one, the relay forwards the call to
 * the browser, `AgentChatService` runs it through this registry, and the string
 * result is sent back so the agent can continue its turn.
 *
 * A client tool is STRICTLY a UI affordance — it must never mutate trading or
 * account state (that is a server tool behind the delegate gateway with the
 * operator's permission gates). These only move the operator's own view around.
 */

/**
 * A client tool's result: either a plain string (wrapped by {@link
 * ClientToolRegistry.execute} in the legacy `{ok:true,result}` envelope) or a
 * structured object (a control ack / rejection) that is serialized VERBATIM as
 * the client_tool_result content — the relay inspects top-level fields such as
 * `rejected`, so those acks must not be double-wrapped.
 */
export type ClientToolResult = string | Record<string, unknown>;

export interface ClientTool {
  readonly name: string;
  readonly description: string;
  /** Executes the tool; returns a string or a structured ack (see {@link ClientToolResult}). */
  readonly execute: (args: Record<string, unknown>) => Promise<ClientToolResult> | ClientToolResult;
}

/**
 * M2-1 bridge — renders validated `show_components` entries into the chat
 * transcript. Supplied by `AgentChatService` (which owns the transcript) via
 * {@link ClientToolRegistry.setComponentsPresenter}, so the registry stays free of
 * a dependency on the chat service (no DI cycle).
 */
export type ComponentsPresenter = (entries: readonly CatalogEntry[]) => void;

/** Mirrors the Copilot filing endpoint's governance-only subset. Execution drafts copy only. */
export const COPILOT_FILEABLE_PROPOSAL_TYPES: readonly string[] = [
  'strategy_param', 'strategy_state', 'new_strategy_pr', 'mode_switch',
];

export function isCopilotFileableType(value: string | undefined): boolean {
  return value !== undefined && COPILOT_FILEABLE_PROPOSAL_TYPES.includes(value);
}

/**
 * M3 — an agent-drafted change request awaiting the operator's Approve / Reject.
 * The operator's decision is a REAL round-trip: Approve/Reject resolves the
 * `draft_change_request` client tool with the decision, so the AGENT observes
 * whether the operator approved or rejected and continues its turn accordingly
 * (closes F4/m3). It is still read-only in the sense that NO trading/account state
 * is mutated here — an approval only surfaces the drafted text for the operator to
 * submit through the existing change-request workflow; the copilot applies nothing.
 */
export interface DraftChangeRequest {
  readonly title: string;
  readonly summary: string;
  readonly rationale: string;
  /**
   * §18 one-click filing — OPTIONAL structured mapping supplied by the agent.
   * A supported governance type and payload permit a separate operator File
   * action after approval. Execution types and unmapped drafts copy only.
   * Approval itself returns the operator's decision; it submits nothing.
   */
  readonly proposalType?: string;
  readonly proposalPayload?: Record<string, unknown>;
}

/**
 * Presents a draft change request to the operator and resolves with the operator's
 * decision as a JSON string (`{"decision":"approved"}` / `{"decision":"rejected",
 * "note":...}`) once they Approve or Reject. It may instead resolve a structured
 * local-cancellation result when the agent stops waiting. Supplied by `AgentChatService`
 * (which owns the transcript the card renders into) via {@link
 * ClientToolRegistry.setDraftPresenter}, so the registry stays free of any
 * dependency on the chat service (no DI cycle).
 */
export type DraftPresenter = (draft: DraftChangeRequest) => Promise<ClientToolResult>;

@Injectable({ providedIn: 'root' })
export class ClientToolRegistry {
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly catalog = inject(AgentComponentCatalogService);
  private readonly dashboardFilters = inject(DashboardFilterStore);
  private readonly tools = new Map<string, ClientTool>();
  /** M3 HITL bridge — set by AgentChatService; renders the draft-CR review card. */
  private draftPresenter: DraftPresenter | null = null;
  /** M2-1 bridge — set by AgentChatService; renders show_components entries. */
  private componentsPresenter: ComponentsPresenter | null = null;

  constructor() {
    this.registerDefaults();
  }

  /** Attach the human-in-the-loop draft-CR presenter (see {@link DraftPresenter}). */
  setDraftPresenter(presenter: DraftPresenter): void {
    this.draftPresenter = presenter;
  }

  /** Attach the generative-UI presenter (see {@link ComponentsPresenter}). */
  setComponentsPresenter(presenter: ComponentsPresenter): void {
    this.componentsPresenter = presenter;
  }

  register(tool: ClientTool): void {
    if (this.tools.has(tool.name)) {
      this.logger.warn(`ClientToolRegistry: overwriting tool ${tool.name}`);
    }
    this.tools.set(tool.name, tool);
  }

  has(name: string): boolean {
    return this.tools.has(name);
  }

  list(): readonly ClientTool[] {
    return [...this.tools.values()];
  }

  /** Execute a client tool by name. Returns a JSON string result (errors are
   * returned, not thrown, so one bad UI action doesn't abort the agent turn). */
  async execute(name: string, args: Record<string, unknown>): Promise<string> {
    const tool = this.tools.get(name);
    if (!tool) {
      return JSON.stringify({
        ok: false,
        rejected: true,
        reason: 'unknown_client_tool',
        error: `unknown client tool: ${name}`,
      });
    }
    try {
      const result = await tool.execute(args ?? {});
      // Structured pass-through: a tool that returns an object (a control ack like
      // `{ok:true}` / `{rendered,unknownTypes}`, or a rejection
      // `{ok:false,rejected:true,reason}`) is serialized VERBATIM so the relay can
      // read its top-level fields. A plain string keeps the legacy envelope.
      if (result !== null && typeof result === 'object') {
        return JSON.stringify(result);
      }
      return JSON.stringify({ ok: true, result });
    } catch (err) {
      this.logger.warn(`ClientToolRegistry: ${name} failed: ${String(err)}`);
      return JSON.stringify({
        ok: false,
        rejected: true,
        reason: 'client_tool_failed',
        error: `client tool ${name} failed: ${String(err)}`,
      });
    }
  }

  private registerDefaults(): void {
    this.register({
      name: 'ui_navigate',
      description:
        'Navigate the operator to an allowlisted in-app route (e.g. "/dashboard", "/risk-management", ' +
        '"/backtests"). Read-only view change. Non-allowlisted targets are rejected.',
      execute: async (args) => {
        const target = String(args['path'] ?? '').trim();
        // M2-3: validate against the app's own operator-facing route table. Any
        // target that is not an allowlisted route (or a child of one) is REFUSED
        // and never navigated — the relay bumps a rejection metric on this shape.
        if (!this.isAllowlistedRoute(target)) {
          this.logger.warn('copilot: ui_navigate rejected non-allowlisted target', {
            component: 'ClientToolRegistry',
            target,
          });
          return { ok: false, rejected: true, reason: 'route_not_allowlisted' };
        }
        const ok = await this.router.navigateByUrl(target);
        if (!ok) {
          return { ok: false, navigated: null, blocked: true, reason: 'guard_or_redirect' };
        }
        const reached = this.router.url;
        // A wildcard component may keep the requested URL. Inspect the settled
        // primary route as well as its URL before claiming the view exists.
        let route: ActivatedRouteSnapshot | undefined = this.router.routerState.snapshot.root;
        while (route) {
          if (route.routeConfig?.path === '**') {
            return { ok: false, rejected: true, reason: 'route_not_found', requested: target, navigated: reached };
          }
          route = route.children.find(child => child.outlet === PRIMARY_OUTLET);
        }
        if (!this.isAllowlistedRoute(reached)) {
          return { ok: false, blocked: true, reason: 'guard_or_redirect', requested: target, navigated: reached };
        }
        return { ok: true, requested: target, navigated: reached };
      },
    });

    this.register({
      name: 'ui_highlight',
      description:
        'Briefly highlight an on-screen element by its data-copilot-id attribute so the operator can find it. ' +
        'The element must be on the CURRENT view (use ui_navigate first). ' +
        describeUiHighlightTargets(),
      execute: (args) => {
        const id = String(args['id'] ?? '').trim();
        if (id.length === 0) {
          return { ok: false, rejected: true, reason: 'missing_element_id' };
        }
        const el = document.querySelector<HTMLElement>(`[data-copilot-id="${CSS.escape(id)}"]`);
        if (!el) {
          return { ok: false, rejected: true, reason: 'element_not_found', id };
        }
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('copilot-highlight');
        setTimeout(() => el.classList.remove('copilot-highlight'), 2500);
        return { ok: true, highlighted: id };
      },
    });

    // M3 — human-in-the-loop draft change request. The agent CALLS this; the
    // browser renders a review card (Approve / Reject) via the presenter and
    // resolves with the operator's decision, which is RETURNED to the agent as
    // this tool's result — so the agent observes approve vs reject. No
    // trading/account state is mutated here: an approval only surfaces the drafted
    // text for the operator to submit through the existing change-request workflow.
    this.register({
      name: 'draft_change_request',
      description:
        'Draft a change request for the operator to review (Approve/Reject). Their decision ' +
        '({"decision":"approved"|"rejected"[,"note":...]}) is returned to you as this tool result, ' +
        'so wait for it before continuing. Approval returns a decision only; it applies and files nothing. ' +
        'Optionally include "proposalType" (one of order|cancel_order|strategy_param|strategy_state|' +
        'new_strategy_pr|mode_switch) and a matching structured "proposalPayload" object. ' +
        'Only strategy_param|strategy_state|new_strategy_pr|mode_switch can be filed as PENDING proposals ' +
        'through a separate operator File action when enabled; order|cancel_order and free-form drafts copy only.',
      execute: async (args) => {
        const rawType = String(args['proposalType'] ?? '').trim();
        const rawPayload = args['proposalPayload'];
        const structuredPayload =
          rawPayload !== null && typeof rawPayload === 'object' && !Array.isArray(rawPayload)
            ? (rawPayload as Record<string, unknown>)
            : undefined;
        const draft: DraftChangeRequest = {
          title: String(args['title'] ?? '').trim(),
          summary: String(args['summary'] ?? '').trim(),
          rationale: String(args['rationale'] ?? '').trim(),
          ...(rawType.length > 0 ? { proposalType: rawType } : {}),
          ...(structuredPayload !== undefined ? { proposalPayload: structuredPayload } : {}),
        };
        if (draft.title.length === 0 && draft.summary.length === 0) {
          return { ok: false, rejected: true, reason: 'empty_change_request_draft' };
        }
        if (!this.draftPresenter) {
          return { ok: false, rejected: true, reason: 'draft_review_ui_not_attached' };
        }
        return this.draftPresenter(draft);
      },
    });

    // M2-1 — generative UI. The agent hands us `{ components: [{type, props}, …] }`;
    // we validate the whole batch against the catalog allowlist and render it only
    // when every entry is valid. Unknown/invalid entries reject the WHOLE batch,
    // matching the accepted spec: never partially render an untrusted payload.
    this.register({
      name: 'show_components',
      description:
        'Render read-only UI widgets inline in the chat from data you already fetched via the read ' +
        `gateway tools. Allowlisted component types: ${this.catalog.allowedTypes().join(', ')}. ` +
        'Shape: {"components":[{"type":"pnl-summary","props":{…}}]}. Unknown or invalid entries reject the batch.',
      execute: (args) => {
        const { entries, rendered, unknownTypes, rejected } = this.catalog.validate(args);
        if (rejected) {
          return {
            ok: false,
            rejected: true,
            reason: 'component_validation_failed',
            rendered: 0,
            unknownTypes,
          };
        }
        if (!this.componentsPresenter) {
          return { ok: false, rejected: true, reason: 'components_ui_not_attached', rendered: 0 };
        }
        this.componentsPresenter(entries);
        return { ok: true, rendered, unknownTypes };
      },
    });

    // M2-2 — open the backtest wizard prefilled via its existing query-param
    // mechanism (the wizard reads `?strategyId=` on init). Read-only navigation.
    this.register({
      name: 'prefill_backtest_wizard',
      description:
        'Open the New Backtest wizard for a known strategy (e.g. {"strategyId":"…"}). ' +
        'Read-only view change — nothing is submitted.',
      execute: async (args) => {
        // Unwrap the legacy sidecar `{params:{...}}` shape during rolling deploys,
        // but emit only the one query param the real wizard consumes.
        const params = this.recordArg(args['params']) ?? args;
        const strategyId = String(params['strategyId'] ?? params['strategy_id'] ?? '').trim();
        if (strategyId.length === 0) {
          return { ok: false, rejected: true, reason: 'missing_strategy_id' };
        }
        const navigated = await this.router.navigate(['/backtests/new'], {
          queryParams: { strategyId },
        });
        return navigated
          ? { ok: true, prefilled: ['strategyId'], strategyId }
          : { ok: false, rejected: true, reason: 'guard_or_redirect' };
      },
    });

    // M2-2 — apply filters to the operator dashboard's shared filter state.
    this.register({
      name: 'set_dashboard_filters',
      description:
        'Apply filters to the operator dashboard (e.g. {"timeframe":"24H","symbol":"BTC/USD"}). ' +
        'Read-only view change.',
      execute: (args) => {
        // Accept the legacy sidecar `{filters:{...}}` wrapper during rolling
        // deploys, then report only keys the dashboard actually applied.
        const filters = this.recordArg(args['filters']) ?? args;
        const applied = this.dashboardFilters.applyFilters(filters);
        return applied.length > 0
          ? { ok: true, applied }
          : { ok: false, rejected: true, reason: 'no_supported_filters', applied };
      },
    });
  }

  /**
   * M2-3: is `target` an allowlisted in-app route? The allowlist is derived from
   * the app's own operator-facing route table ({@link getOperatorFacingRoutePaths}),
   * so it can never drift from the real routes. A target matches when it equals an
   * allowlisted path or is a child of one (`/trading/positions` ⊂ `/trading`);
   * query string / fragment are stripped before matching.
   */
  private isAllowlistedRoute(target: string): boolean {
    if (!target.startsWith('/')) {
      return false;
    }
    // Strip query + fragment; reject protocol-relative / external-looking targets.
    const path = target.split(/[?#]/, 1)[0];
    if (path.startsWith('//')) {
      return false;
    }
    const allowlist = getOperatorFacingRoutePaths();
    return allowlist.some((route) => path === route || path.startsWith(`${route}/`));
  }

  /** Narrow an unknown tool argument to a plain record. */
  private recordArg(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }
}
